const pricingDisclaimer =
  'Pricing examples are based on publicly available pricing and typical nonprofit fee structures as of 2026. Actual costs vary by contract, payment method, and organization. Always confirm pricing with the provider.';

const comparisonPages = {
  classy: {
    slug: 'classy',
    competitor: 'Classy',
    competitorAlt: 'Classy (GoFundMe Pro)',
    title: 'PurelyGive vs Classy | Nonprofit Donation Platform Comparison',
    description:
      'Compare PurelyGive vs Classy. See typical platform fees, processing costs, and how much nonprofits may receive from each $100 donation.',
    shareTitle: 'PurelyGive vs Classy',
    shareDescription:
      'Compare fees, complexity, and how much your nonprofit keeps from each donation.',
    shareImageText: 'PurelyGive vs Classy',
    keywordPhrase: 'Classy alternative',
    competitorOverview:
      'Classy is an enterprise fundraising platform widely used by larger nonprofits and teams running complex campaigns.',
    quickComparison: [
      { feature: 'Platform fee', purelygive: '0%', competitor: 'Typically 1–5% depending on contract' },
      { feature: 'Subscription', purelygive: '$0', competitor: 'Starts around $299/month (varies by organization)' },
      { feature: 'Processing fees', purelygive: 'Covered by PurelyGive', competitor: 'Typically ~2.2–2.4% + $0.30 depending on payment processor' },
      { feature: 'Net received per $100 (example)', purelygive: '$100.00', competitor: '$94.50' },
      { feature: 'Best for', purelygive: 'Simple donation pages and campaigns', competitor: 'Large campaigns and enterprise teams' },
    ],
    donationExample: {
      donationAmount: 100,
      purelyGiveNet: 100,
      competitorNet: 94.5,
      detailRows: [
        { feeType: 'Platform fee (typical 3%)', feeValue: '$3.00' },
        { feeType: 'Processing (typical)', feeValue: '$2.50' },
        { feeType: 'Total', feeValue: '~$5.50' },
      ],
    },
    competitorAnnualFeeRate: 0.055,
    strengths: [
      'Peer-to-peer fundraising',
      'Event fundraising',
      'Corporate matching',
      'Salesforce integrations',
      'Enterprise reporting',
    ],
    featuresTitle: 'Why nonprofits choose Classy',
    featuresIntro: 'Classy focuses on enterprise fundraising features like:',
    purelyGiveStrengths: [
      'Keep more of each donation',
      'Launch quickly',
      'Avoid platform subscription costs',
      'Simpler day-to-day management',
    ],
    purelyGiveBestIf: [
      'You want simple donation setup',
      'You want to maximize net donations retained',
      'You prefer a lightweight admin workflow',
    ],
    competitorBestIf: [
      'You run large multi-team fundraising programs',
      'You need enterprise reporting and complex integrations',
      'You rely on advanced campaign infrastructure',
    ],
    bestFitOneLiner:
      'If you run enterprise campaigns and events, Classy may be a better fit. If you want maximum net donations with simple setup, PurelyGive is a better fit.',
    faq: [
      {
        question: 'What are Classy platform fees?',
        answer:
          'Classy pricing varies by plan and contract. Nonprofits commonly report subscription costs plus processing fees and, in some cases, platform fees.',
      },
      {
        question: 'How much does Classy cost per donation?',
        answer:
          'Total cost per donation depends on your contract terms and payment processing setup, so it can vary by organization and donation method.',
      },
      {
        question: 'Does PurelyGive charge platform fees?',
        answer:
          'PurelyGive uses a donor-supported model so nonprofits can receive the full donation amount, with no platform subscription fees and optional donor coverage of transaction costs.',
      },
      {
        question: 'Is PurelyGive a Classy alternative?',
        answer:
          'Yes. Nonprofits often evaluate PurelyGive as a Classy alternative when they want simple setup, predictable costs, and stronger net donation retention.',
      },
      {
        question: 'Can PurelyGive support campaigns, events, or recurring donations?',
        answer:
          'Yes. PurelyGive supports core campaign and recurring giving workflows, and teams with advanced multi-program fundraising needs may still prefer enterprise platforms.',
      },
      {
        question: 'How quickly can we switch donation platforms?',
        answer: 'Most organizations can launch with PurelyGive quickly using guided onboarding and ready-to-use donation forms.',
      },
    ],
  },
  donorbox: {
    slug: 'donorbox',
    competitor: 'Donorbox',
    title: 'PurelyGive vs Donorbox | Donation Platform Fees Compared',
    description:
      'Compare PurelyGive and Donorbox donation platforms. Understand typical fees, donor tip models, and which platform keeps more money for your nonprofit.',
    shareTitle: 'PurelyGive vs Donorbox',
    shareDescription:
      'Compare fees, complexity, and how much your nonprofit keeps from each donation.',
    shareImageText: 'PurelyGive vs Donorbox',
    keywordPhrase: 'Donorbox alternative',
    competitorOverview: 'Donorbox is a popular donation platform for small to mid-size nonprofits seeking quick setup.',
    quickComparison: [
      { feature: 'Platform fee', purelygive: '0%', competitor: 'Standard plan: 2.95%' },
      { feature: 'Subscription', purelygive: '$0', competitor: 'Pro plan: $150/month (optional)' },
      { feature: 'Processing fees', purelygive: 'Covered by PurelyGive', competitor: 'Typical nonprofit rates: 2.2–2.9% + $0.30' },
      { feature: 'Net received per $100 (example)', purelygive: '$100.00', competitor: '$94.00' },
      { feature: 'Best for', purelygive: 'Simple zero-fee giving', competitor: 'Small nonprofits wanting easy setup' },
    ],
    bestFitOneLiner:
      'If you want familiar SMB-focused fundraising tooling, Donorbox may be a better fit. If you want maximum net donations with simple setup, PurelyGive is a better fit.',
    donationExample: {
      donationAmount: 100,
      purelyGiveNet: 100,
      competitorNet: 94,
      detailRows: [
        { feeType: 'Platform fee (2.95%)', feeValue: '$2.95' },
        { feeType: 'Processing (typical)', feeValue: '$3.05' },
        { feeType: 'Total', feeValue: '~$6.00' },
      ],
    },
    competitorAnnualFeeRate: 0.06,
    strengths: ['Donation forms', 'Recurring giving', 'Campaign pages', 'Integrations'],
    purelyGiveStrengths: [
      'Keep more of each donation',
      'Launch quickly',
      'Avoid platform subscription costs',
      'Simpler day-to-day management',
    ],
    purelyGiveBestIf: ['You want simple donation setup', 'You want to maximize net donations retained', 'You prefer a lightweight admin workflow'],
    competitorBestIf: ['You already use Donorbox integrations', 'You prefer a familiar SMB-focused fundraising tool'],
    faq: [
      {
        question: 'What are Donorbox fees?',
        answer: 'Donorbox typically charges a platform fee on standard plans, with pricing that can vary by plan and processor setup.',
      },
      {
        question: 'Are donor tips required on PurelyGive?',
        answer: 'No. Donor support is optional, and PurelyGive is designed so nonprofits can receive the full donation amount through its donor-supported model.',
      },
      {
        question: 'What types of nonprofits are the best fit for PurelyGive?',
        answer:
          'PurelyGive is typically a strong fit for nonprofits that want straightforward donation operations, predictable economics, and high net donation retention.',
      },
      {
        question: 'Can we export donor data?',
        answer: 'Yes. PurelyGive supports exporting donor and transaction data for your reporting workflows.',
      },
    ],
  },
  givebutter: {
    slug: 'givebutter',
    competitor: 'Givebutter',
    title: 'PurelyGive vs Givebutter | Best Donation Platform for Nonprofits',
    description:
      'Compare PurelyGive vs Givebutter to understand platform fee options, processing costs, and total donation value retained by nonprofits.',
    shareTitle: 'PurelyGive vs Givebutter',
    shareDescription:
      'Compare fees, complexity, and how much your nonprofit keeps from each donation.',
    shareImageText: 'PurelyGive vs Givebutter',
    keywordPhrase: 'Givebutter alternative',
    competitorOverview:
      'Givebutter supports donor-covered fees with optional tips, while also offering a fee-based mode when tips are disabled.',
    quickComparison: [
      { feature: 'Platform fee', purelygive: '0%', competitor: '0–3% (varies by tip settings)' },
      { feature: 'Subscription', purelygive: '$0', competitor: '$0 core subscription' },
      { feature: 'Processing fees', purelygive: 'Covered by PurelyGive', competitor: 'Typical processor rates around 2.9% + $0.30' },
      { feature: 'Net received per $100 (example)', purelygive: '$100.00', competitor: '$96.70' },
      { feature: 'Best for', purelygive: 'Simple donations', competitor: 'Campaigns and events' },
    ],
    bestFitOneLiner:
      'If you run event-heavy fundraising, Givebutter may be a better fit. If you want maximum net donations with simple setup, PurelyGive is a better fit.',
    donationExample: {
      donationAmount: 100,
      purelyGiveNet: 100,
      competitorNet: 96.7,
      detailRows: [
        { feeType: 'Tips enabled scenario', feeValue: 'Can be $0 platform fee' },
        { feeType: 'Tips disabled platform fee', feeValue: '3%' },
        { feeType: 'Typical net received', feeValue: 'Varies by settings' },
      ],
    },
    competitorAnnualFeeRate: 0.033,
    strengths: ['Peer-to-peer fundraising', 'Event ticketing', 'Auctions', 'Campaign pages'],
    purelyGiveStrengths: [
      'Keep more of each donation',
      'Launch quickly',
      'Avoid platform subscription costs',
      'Simpler day-to-day management',
    ],
    purelyGiveBestIf: ['You want simple donation setup', 'You want to maximize net donations retained', 'You prefer a lightweight admin workflow'],
    competitorBestIf: ['You run event-heavy campaigns', 'You want auction tooling in one platform'],
    faq: [
      {
        question: 'Does Givebutter support donor-covered fees?',
        answer: 'Yes. Givebutter supports optional tips that may offset platform fees, and pricing varies by plan and tip settings.',
      },
      {
        question: 'How does PurelyGive compare on net donation?',
        answer: 'PurelyGive is designed so nonprofits can receive the full donation amount through its donor-supported model, with optional donor coverage of transaction costs.',
      },
      {
        question: 'Does PurelyGive replace platforms like Givebutter for every nonprofit?',
        answer:
          'Not always. Nonprofits running complex event or auction-led programs may still prefer Givebutter, while organizations prioritizing donation retention and simplicity often choose PurelyGive.',
      },
      {
        question: 'Is migration difficult?',
        answer: 'Most nonprofits can move campaigns and begin accepting donations quickly with guided setup.',
      },
    ],
  },
  stripe: {
    slug: 'stripe',
    competitor: 'Stripe',
    title: 'PurelyGive vs Stripe | Stripe Donations vs PurelyGive',
    description:
      'Compare PurelyGive vs Stripe for nonprofit donations. Review processing costs, platform tooling, and what your organization typically keeps.',
    shareTitle: 'PurelyGive vs Stripe',
    shareDescription:
      'Compare fees, complexity, and how much your nonprofit keeps from each donation.',
    shareImageText: 'PurelyGive vs Stripe',
    keywordPhrase: 'Stripe alternative',
    competitorOverview:
      'Stripe is a payment processor rather than a full donation platform, often chosen by developer-driven teams.',
    quickComparison: [
      { feature: 'Platform fee', purelygive: '0%', competitor: 'None (processor only)' },
      { feature: 'Subscription', purelygive: '$0', competitor: '$0 (custom stack build required)' },
      { feature: 'Processing fees', purelygive: 'Covered by PurelyGive', competitor: 'Typical nonprofit rate: 2.2% + $0.30' },
      { feature: 'Net received per $100 (example)', purelygive: '$100.00', competitor: '$97.50' },
      { feature: 'Best for', purelygive: 'Ready-to-launch giving', competitor: 'Developer-driven organizations' },
    ],
    bestFitOneLiner:
      'If you have developer resources and want to build custom flows, Stripe may be a better fit. If you want maximum net donations with simple setup, PurelyGive is a better fit.',
    donationExample: {
      donationAmount: 100,
      purelyGiveNet: 100,
      competitorNet: 97.5,
      detailRows: [
        { feeType: 'Processing fee', feeValue: '$2.50' },
        { feeType: 'Platform tools', feeValue: 'Not included by default' },
        { feeType: 'Total', feeValue: '$2.50' },
      ],
    },
    competitorAnnualFeeRate: 0.025,
    strengths: ['Flexible APIs', 'Developer tooling', 'Payment infrastructure'],
    purelyGiveStrengths: [
      'Keep more of each donation',
      'Launch quickly',
      'Avoid platform subscription costs',
      'Simpler day-to-day management',
    ],
    purelyGiveBestIf: ['You want simple donation setup', 'You want to maximize net donations retained', 'You prefer a lightweight admin workflow'],
    competitorBestIf: ['You have developer resources', 'You want to build and maintain a custom donation stack'],
    faq: [
      {
        question: 'Is Stripe a donation platform?',
        answer: 'Stripe is primarily a payment processor. Nonprofits may need additional tools for donor CRM, donation pages, and campaigns.',
      },
      {
        question: 'Can we export donor data from PurelyGive?',
        answer: 'Yes. Export capabilities are available to support nonprofit reporting and reconciliation.',
      },
      {
        question: 'Can PurelyGive support campaigns, events, or recurring donations?',
        answer:
          'Yes. PurelyGive includes core fundraising workflows out of the box so most nonprofits can launch without building and maintaining a custom stack.',
      },
      {
        question: 'What setup time should we expect?',
        answer: 'PurelyGive setup is typically fast because core fundraising features are built in.',
      },
    ],
  },
};

export { pricingDisclaimer };
export default comparisonPages;
